<template>
  <div>
    <SoDivider class="mb-4" />
    <div class="form-layout">
      <SoForm ref="form" v-model="isValid" class="form-fields" @submit.prevent>
        <template v-if="!lockedCommunication">
          <SoSelect
            v-model="formData.communicationId"
            label="Communication Channel"
            :items="communicationItems"
            :rules="[rules.required]"
            placeholder="Select a communication"
            data-testid="communication-select"
          />
          <div
            class="text-caption text-medium-emphasis mt-n1"
            data-testid="communication-hint"
          >
            Showing communication channels without any existing email templates.
          </div>
        </template>
        <SoTextField
          v-model="formData.subject"
          label="Subject"
          :rules="[rules.required]"
          data-testid="subject-input"
        />
        <SoCodeEditor
          v-model="formData.htmlContent"
          label="HTML content"
          :rows="18"
          :rules="[rules.required]"
          data-test-id="html-content-input"
        />
        <SoTextarea
          v-model="formData.plainContent"
          label="Plain-text content"
          :rules="[rules.required]"
          data-testid="plain-content-input"
        />
        <SoTextField
          v-model="formData.modifiedReason"
          label="Reason for change (optional)"
          data-testid="modified-reason-input"
        />
        <SoCheckbox
          v-model="confirmedRepoUpdate"
          :rules="[rules.isTruthy]"
          data-testid="repo-updated-checkbox"
        >
          <template #label>
            <span>
              I have updated the MJML files in the
              <a
                href="https://github.com/soenergy/email-templates"
                target="_blank"
                rel="noopener"
                class="text-primary"
                @click.stop
              >
                email-templates repository
              </a>
            </span>
          </template>
        </SoCheckbox>
      </SoForm>

      <div class="form-preview">
        <EmailTemplatePreview
          fill-height
          hide-html-source
          :html-content="formData.htmlContent"
          :plain-content="formData.plainContent"
        >
          <template #headerLeft>
            <EmailTemplateValidationSummary
              :findings="validationFindings"
              :assets="templateAssets"
              :variables="templateVariables"
              :links="templateLinks"
              :checking-assets="checkingAssets"
            />
          </template>
        </EmailTemplatePreview>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { EmailTemplatesService } from "@/services/admin/EmailTemplatesService"
  import { EmailTemplatesMutator } from "@/mutators/emailTemplates/EmailTemplatesMutator"
  import { AuthService } from "@/services/AuthService"
  import { useSnackbarStore } from "@/stores/snackbar"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoDivider from "@/components/SoDivider.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoTextarea from "@/components/formControls/SoTextarea.vue"
  import SoCodeEditor from "@/components/formControls/SoCodeEditor.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import EmailTemplatePreview from "./EmailTemplatePreview.vue"
  import EmailTemplateValidationSummary from "./EmailTemplateValidationSummary.vue"
  import { EmailTemplateValidationService } from "@/services/admin/emailTemplateValidation/EmailTemplateValidationService"
  import {
    AssetCheck,
    ValidationFinding,
  } from "@/services/admin/emailTemplateValidation/emailTemplateValidation.types"
  import { debounce, type DebouncedFunc } from "lodash-es"
  import rules from "@/validators/rules"
  import { SoSelectItem } from "@/types/components/SoSelect"
  import { nextVersion } from "../helpers/emailTemplateRows"
  import {
    ICreateEmailTemplateInput,
    IEmailTemplate,
  } from "@/models/admin/emailTemplates/emailTemplates.types"
  import { toSyncPayload } from "../helpers/emailTemplateSync"
  import { QueryResult } from "@/api/utils/types"

  const VALIDATION_DEBOUNCE_MS = 400

  export default defineComponent({
    components: {
      SoForm,
      SoDivider,
      SoTextField,
      SoTextarea,
      SoCodeEditor,
      SoSelect,
      SoCheckbox,
      EmailTemplatePreview,
      EmailTemplateValidationSummary,
    },
    emits: ["success", "version-change", "communication-change", "submitting"],
    data() {
      const lockedParam = this.$route.query.communicationId
      const lockedCommunication = lockedParam ? Number(lockedParam) : null
      const cloneParam = this.$route.query.cloneFromId
      const cloneFromId = cloneParam ? Number(cloneParam) : null
      return {
        rules,
        lockedCommunication,
        cloneFromId,
        prefilled: false,
        snackbar: useSnackbarStore(),
        communicationsQuery: EmailTemplatesService.getCommunications(),
        summariesQuery: EmailTemplatesService.getEmailTemplateSummaries(),
        versionsQuery: null as QueryResult<IEmailTemplate[]> | null,
        createMutator: EmailTemplatesMutator.createTemplate(),
        createLiveMutator: EmailTemplatesMutator.createTemplateAsLive(),
        isValid: false,
        isSubmitting: false,
        confirmedRepoUpdate: false,
        pristineSnapshot: "",
        saved: false,
        staticFindings: [] as ValidationFinding[],
        templateVariables: [] as string[],
        templateLinks: [] as string[],
        templateAssets: [] as AssetCheck[],
        checkingAssets: false,
        revalidateDebounced: null as DebouncedFunc<() => void> | null,
        formData: {
          communicationId: lockedCommunication,
          subject: "",
          htmlContent: "",
          plainContent: "",
          modifiedReason: "",
        },
      }
    },
    computed: {
      communicationName(): string {
        return (
          (this.communicationsQuery.data ?? []).find(
            (communication) =>
              communication.id === this.formData.communicationId,
          )?.name ?? ""
        )
      },
      modifiedBy(): string {
        return (
          AuthService.getCurrentUser().name ||
          AuthService.getCurrentUser().username ||
          ""
        )
      },
      // Communications that already have at least one template version.
      communicationIdsWithTemplates(): Set<number> {
        return new Set(
          (this.summariesQuery.data ?? []).map(
            (template) => template.communicationId,
          ),
        )
      },
      communicationItems(): SoSelectItem[] {
        const communications = this.communicationsQuery.data ?? []
        if (this.lockedCommunication) {
          return communications.map((communication) => ({
            title: communication.name,
            value: communication.id,
          }))
        }
        // Wait for the summaries query so already-templated channels are
        // excluded before any communication becomes selectable.
        if (!this.summariesQuery.isFetched) return []
        return communications
          .filter(
            (communication) =>
              !this.communicationIdsWithTemplates.has(communication.id),
          )
          .map((communication) => ({
            title: communication.name,
            value: communication.id,
          }))
      },
      // Source version to clone content from when creating a new version.
      cloneSource(): IEmailTemplate | null {
        if (!this.cloneFromId) return null
        return (
          (this.versionsQuery?.data ?? []).find(
            (template) => template.id === this.cloneFromId,
          ) ?? null
        )
      },
      // The existing versions must be loaded from the server (not placeholder
      // data) before we can compute a correct next version number.
      versionsReady(): boolean {
        return (
          !!this.versionsQuery &&
          this.versionsQuery.isFetched &&
          !this.versionsQuery.isPlaceholderData
        )
      },
      versionLabel(): string {
        if (!this.formData.communicationId || !this.versionsReady) return ""
        const next = nextVersion(
          this.versionsQuery?.data ?? [],
          this.formData.communicationId,
        )
        return `Version ${next}`
      },
      formSnapshot(): string {
        return JSON.stringify({
          communicationId: this.formData.communicationId,
          subject: this.formData.subject,
          htmlContent: this.formData.htmlContent,
          plainContent: this.formData.plainContent,
          modifiedReason: this.formData.modifiedReason,
          confirmedRepoUpdate: this.confirmedRepoUpdate,
        })
      },
      isDirty(): boolean {
        return !this.saved && this.formSnapshot !== this.pristineSnapshot
      },
      validationFindings(): ValidationFinding[] {
        return [
          ...this.staticFindings,
          ...EmailTemplateValidationService.assetFindings(this.templateAssets),
        ]
      },
      validationContent(): string {
        return `${this.formData.htmlContent} ${this.formData.plainContent}`
      },
    },
    watch: {
      validationContent() {
        this.revalidateDebounced?.()
      },
      versionLabel: {
        immediate: true,
        handler(label: string) {
          this.$emit("version-change", label)
        },
      },
      communicationName: {
        immediate: true,
        handler(name: string) {
          this.$emit(
            "communication-change",
            this.lockedCommunication ? name : "",
          )
        },
      },
      "formData.communicationId": {
        immediate: true,
        handler(communicationId: number | null) {
          this.versionsQuery = communicationId
            ? EmailTemplatesService.getEmailTemplatesByCommunication(
                communicationId,
              )
            : null
        },
      },
      cloneSource: {
        immediate: true,
        handler(template: IEmailTemplate | null) {
          if (!template || this.prefilled) return
          this.prefilled = true
          // Clone data can resolve after the user has started typing; don't
          // clobber their input in that case.
          const pristine =
            !this.formData.subject &&
            !this.formData.htmlContent &&
            !this.formData.plainContent
          if (!pristine) return
          this.formData.subject = template.subject
          this.formData.htmlContent = template.htmlContent
          this.formData.plainContent = template.plainContent
          // Cloned-but-untouched content isn't an unsaved change.
          this.pristineSnapshot = this.formSnapshot
        },
      },
    },
    created() {
      this.revalidateDebounced = debounce(
        this.revalidate,
        VALIDATION_DEBOUNCE_MS,
      )
    },
    mounted() {
      this.pristineSnapshot = this.formSnapshot
      this.revalidate()
      window.addEventListener("beforeunload", this.onBeforeUnload)
    },
    beforeUnmount() {
      this.revalidateDebounced?.cancel()
      window.removeEventListener("beforeunload", this.onBeforeUnload)
    },
    methods: {
      revalidate() {
        this.staticFindings = EmailTemplateValidationService.runStaticChecks({
          htmlContent: this.formData.htmlContent,
          plainContent: this.formData.plainContent,
        })
        this.templateVariables =
          EmailTemplateValidationService.extractVariables(
            this.formData.htmlContent,
            this.formData.plainContent,
          )
        this.templateLinks = EmailTemplateValidationService.extractLinks(
          this.formData.htmlContent,
          this.formData.plainContent,
        )
        this.checkAssets()
      },
      async checkAssets() {
        this.checkingAssets = true
        // Drop stale results so the summary reflects only the current content.
        this.templateAssets = []
        try {
          this.templateAssets =
            await EmailTemplateValidationService.checkAssets({
              htmlContent: this.formData.htmlContent,
              plainContent: this.formData.plainContent,
            })
        } finally {
          this.checkingAssets = false
        }
      },
      onBeforeUnload(event: BeforeUnloadEvent) {
        if (!this.isDirty) return
        event.preventDefault()
      },
      async validate(): Promise<boolean> {
        const form = this.$refs.form as InstanceType<typeof SoForm>
        const { valid } = await form.validate()
        return valid
      },
      async handleSubmit(makeLive: boolean) {
        if (!(await this.validate())) return

        const communicationId = this.formData.communicationId
        if (!communicationId) return
        if (!this.versionsReady) {
          this.snackbar.addInfo(
            "Still loading template details — please try again in a moment.",
          )
          return
        }

        this.isSubmitting = true
        this.$emit("submitting", true, makeLive)
        try {
          const input: ICreateEmailTemplateInput = {
            communicationId,
            version: nextVersion(
              this.versionsQuery?.data ?? [],
              communicationId,
            ),
            isLive: makeLive,
            subject: this.formData.subject,
            plainContent: this.formData.plainContent,
            htmlContent: this.formData.htmlContent,
            modifiedBy: this.modifiedBy || null,
            modifiedReason: this.formData.modifiedReason,
          }

          const mutator = makeLive ? this.createLiveMutator : this.createMutator
          await mutator.mutateAsync(input)
          this.snackbar.addSuccess(
            makeLive ? "Email template published" : "Email template saved",
          )
          // Suppress the unsaved-changes guard for the post-save redirect.
          this.saved = true
          this.$emit(
            "success",
            communicationId,
            input.version,
            toSyncPayload(
              { ...input, communicationName: this.communicationName },
              makeLive,
            ),
          )
        } catch {
          this.snackbar.addError("Failed to save email template")
          // Refetch so a retry recomputes the next version from current data
          // rather than colliding on the same stale version number.
          this.versionsQuery?.refetch()
        } finally {
          this.isSubmitting = false
          this.$emit("submitting", false, makeLive)
        }
      },
    },
  })
</script>

<style scoped>
  .form-layout {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
    gap: 32px;
    align-items: start;
  }
  @media (max-width: 960px) {
    .form-layout {
      grid-template-columns: 1fr;
    }
  }
  .form-fields {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .form-preview {
    position: sticky;
    top: 16px;
    /* Fill the viewport below the nav + page header without overflowing it. */
    height: calc(100vh - 270px);
    min-height: 500px;
  }
</style>
